<template>
  <div class="page-reset_password com_bg1">
    <div id="wrapper1">
      <img
        src="/WITH_AG_PORTAL/images/with-kaunet_logo2.png"
        alt="ウィズカウネット エージェントポータル"
        class="with-kaunet_logo2"
      />
      <div v-if="isError" class="com_form_contet1">
        <!--エラーメッセージエリア-->
        <div class="inner1">
          <h2 class="title1">パスワード変更</h2>
          <div class="error-message">
            <p class="com_center1 com_mgt40">{{ errorMessage }}</p>
          </div>
        </div>
      </div>
      <div v-else class="com_form_contet1">
        <div v-if="isSuccessUpdate" class="inner1">
          <!--パスワード変更完了エリア-->
          <h2 class="title1 com_mgb60">パスワード変更完了</h2>
          <p class="com_center1 com_mgb30">パスワードの変更が完了しました。</p>

          <router-link to="/login" class="com_button1 com_mgb50"
            >ログインページ</router-link
          >
        </div>
        <div v-else class="inner1">
          <!--パスワード変更エリア-->
          <h2 class="title1">パスワード変更</h2>

          <div class="form1">
            <div class="group1">
              <label for="loginId">ログインID</label>
              <input type="text" id="loginId" readonly v-model="loginId" />
            </div>
            <div class="group1">
              <label for="newPassword">新しいパスワード</label>
              <div class="ch_input_password1">
                <input
                  :type="isPasswordVisible ? 'text' : 'password'"
                  id="newPassword"
                  v-model="password"
                  maxlength="20"
                />
                <span
                  class="toggle_icon1"
                  @click="togglePasswordVisibility('password')"
                >
                  <img class="pwdIcon" :src="currentPassworIcon" />
                </span>
              </div>
            </div>
            <div class="group1">
              <label for="newPasswordConfirm">パスワード確認</label>
              <div class="ch_input_password1">
                <input
                  :type="isConfirmPasswordVisible ? 'text' : 'password'"
                  id="newPasswordConfirm"
                  v-model="confirmPassword"
                  maxlength="20"
                />
                <span @click="togglePasswordVisibility('confirmPassword')">
                  <img class="pwdIcon" :src="currentConfirmPassworIcon" />
                </span>
              </div>
            </div>
            <p class="com_fs14 com_mgt20">
              パスワードは半角英数14文字以上20文字以内で、<br />
              大文字・小文字・数字・記号（#-@_.）をすべて含めてください。
            </p>
            <!-- エラーメッセージの表示エリア -->
            <div v-if="errorMessage" class="error-message-2">
              {{ errorMessage }}
            </div>
            <button type="submit" @click="pwdUpdate" :disabled="isProcessing">
              パスワード変更
            </button>
            <div class="com_center1">
              <router-link to="/login" class="com_link1 com_fs15 arr1"
                >ログインページへ戻る</router-link
              >
            </div>
          </div>
        </div>
      </div>
      <p class="com_copyright1">© 2025 Kaunet</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { AxiosError } from "axios";
import apiClient from "@/util/apiClient";
import { formatMessage, outputDebugLogs } from "@/Common";
import messages from "@/assets/messages.json";
import visibleIcon from "@/assets/common/img/icon-eye1.png";
import hiddenIcon from "@/assets/common/img/icon-eye1-slash.png";
import { AppConstants } from "@/constants/AppConstants";

export default defineComponent({
  name: "PwdUpdateComponent",
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const password = ref("");
    const confirmPassword = ref("");
    const errorMessage = ref("");
    const isError = ref(true);
    const isSuccessUpdate = ref(false);
    const loginId = ref("");
    const agCode = ref("");
    const userId = ref("");
    const isPasswordVisible = ref(false);
    const isConfirmPasswordVisible = ref(false);
    const isProcessing = ref(false);

    const togglePasswordVisibility = (field: string) => {
      if (field === "password") {
        isPasswordVisible.value = !isPasswordVisible.value;
      } else if (field === "confirmPassword") {
        isConfirmPasswordVisible.value = !isConfirmPasswordVisible.value;
      }
    };

    // パスワードの目アイコンの制御
    const currentPassworIcon = computed(() => {
      return isPasswordVisible.value ? visibleIcon : hiddenIcon;
    });

    // 確認用パスワードの目アイコンの制御
    const currentConfirmPassworIcon = computed(() => {
      return isConfirmPasswordVisible.value ? visibleIcon : hiddenIcon;
    });

    onMounted(async () => {
      try {
        if (!props.token) {
          // パラメータがなければエラーとする
          errorMessage.value = messages.ERR_USER005;
          return;
        }

        // ワンタイムURL認証APIにてワンタイムURLを検証してユーザー情報を取得
        const response = await apiClient.post(
          "/api/pwdReset/ValidateOtu",
          JSON.stringify(props.token)
        );

        // デバックログ
        outputDebugLogs("ワンタイムURL認証OK");

        // パスワード変更用にユーザー情報を格納
        agCode.value = response.data.user.agCode;
        userId.value = response.data.user.userId;
        loginId.value = response.data.user.loginId;

        // フラグOFFにして、パスワード変更エリアを表示
        isError.value = false;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 401:
              errorMessage.value = messages.ERR_USER005;
              break;
            case 500:
              errorMessage.value = messages.ERR_SYS999;
              break;
            default:
              errorMessage.value = messages.ERR_SYS001;
          }
        } else if (axiosError.request) {
          errorMessage.value = messages.ERR_SYS001;
        } else {
          errorMessage.value = messages.ERR_SYS002;
          if (process.env.VUE_APP_DEBUG === "true") {
            console.log("Error: ", axiosError.message);
          }
        }
      }
    });

    // 文字列が指定の条件を満たしているかチェックする関数
    const isValidPassword = (input: string): boolean => {
      // 各要件に対応する正規表現を定義
      const hasUpperCase = /[A-Z]/; // 大文字
      const hasLowerCase = /[a-z]/; // 小文字
      const hasNumber = /[0-9]/; // 数字
      const hasAllowedSpecialChar = /[#\-_@.]/; // 許可された記号

      // 許可された記号以外が含まれていないかを確認
      const hasOnlyAllowedSpecialChars = /^[A-Za-z0-9#\-_@.]*$/.test(input);

      // 各条件をチェック
      const containsUpperCase = hasUpperCase.test(input);
      const containsLowerCase = hasLowerCase.test(input);
      const containsNumber = hasNumber.test(input);
      const containsSpecialChar = hasAllowedSpecialChar.test(input);

      // 許可された記号のみで、すべての条件を満たしている場合に true を返す
      return (
        containsUpperCase &&
        containsLowerCase &&
        containsNumber &&
        containsSpecialChar &&
        hasOnlyAllowedSpecialChars
      );
    };

    // パスワードの入力チェック
    const passwordValidate = (): boolean => {
      const isNotEmpty = (str: string) => str.trim().length > 0;
      const isAlphanumeric = (str: string) => /^[a-zA-Z0-9]*$/.test(str);
      const isLengthValid = (str: string) =>
        str.length >= 14 && str.length <= 20;
      const areEqual = (str1: string, str2: string) => str1 === str2;

      // 未入力チェック
      if (!isNotEmpty(password.value)) {
        errorMessage.value = formatMessage(messages.ERR_0001, "パスワード");
        return false;
      }

      if (!isNotEmpty(confirmPassword.value)) {
        errorMessage.value = formatMessage(messages.ERR_0001, "パスワード確認");
        return false;
      }
      // 半角英数字チェック
      // if (!isAlphanumeric(password.value)) {
      //   errorMessage.value = messages.ERR_0019;
      //   return false;
      // }

      // if (!isAlphanumeric(confirmPassword.value)) {
      //   errorMessage.value = messages.ERR_0019;
      //   return false;
      // }

      // パスワードの長さチェック（14桁以上20桁以下）
      if (!isLengthValid(password.value)) {
        errorMessage.value = messages.ERR_0019;
        return false;
      }

      if (!isLengthValid(confirmPassword.value)) {
        errorMessage.value = messages.ERR_0019;
        return false;
      }

      // パスワード形式チェック
      // 文字種：英字(大文字・小文字)、数字、記号(「#」「-」「@」「_」「.」)の4種全ての利用が必要
      if (!isValidPassword(password.value)) {
        errorMessage.value = messages.ERR_0031;
        return false;
      }

      if (!isValidPassword(confirmPassword.value)) {
        errorMessage.value = messages.ERR_0031;
        return false;
      }

      // パスワードと確認用パスワードの一致チェック
      if (!areEqual(password.value, confirmPassword.value)) {
        errorMessage.value = formatMessage(
          messages.ERR_0007,
          "パスワード",
          "パスワード確認"
        );
        return false;
      }

      return true;
    };

    // パスワード変更処理
    const pwdUpdate = async () => {
      isProcessing.value = true;
      try {
        //パスワードの入力チェック
        if (!passwordValidate()) {
          return;
        }

        outputDebugLogs("パスワード変更API実行");

        // パスワード変更APIでパスワードを変更
        await apiClient.post("/api/pwdReset/PasswordUpdate", {
          systemKbn: AppConstants.SYSTEM_KBN_WITH,
          agCode: agCode.value,
          userId: userId.value,
          newPassword: password.value,
          otu: props.token,
        });

        // デバックログ
        outputDebugLogs("パスワード変更完了");

        isSuccessUpdate.value = true;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 500:
              errorMessage.value = messages.ERR_SYS999;
              break;
            default:
              errorMessage.value = messages.ERR_SYS001;
          }
        } else if (axiosError.request) {
          errorMessage.value = messages.ERR_SYS001;
        } else {
          errorMessage.value = messages.ERR_SYS002;
          if (process.env.VUE_APP_DEBUG === "true") {
            console.log("Error: ", axiosError.message);
          }
        }
      } finally {
        isProcessing.value = false;
      }
    };

    return {
      password,
      confirmPassword,
      pwdUpdate,
      isError,
      errorMessage,
      loginId,
      agCode,
      userId,
      isPasswordVisible,
      isConfirmPasswordVisible,
      togglePasswordVisibility,
      currentPassworIcon,
      currentConfirmPassworIcon,
      isSuccessUpdate,
      isProcessing,
    };
  },
});
</script>
<style scoped>
.pwdIcon {
  background-size: 28px auto;
  cursor: pointer;
  position: absolute;
  height: 50%;
  right: 10px;
  top: 13px;
  width: 30px;
}

input::-ms-reveal {
  visibility: hidden;
}

.form1 p {
  margin-top: 0;
  margin-bottom: 0;
  margin-left: 159px;
}

.form1 .error-message-2 {
  color: red;
  font-weight: bold;
  margin-top: 1em;
  margin-left: 159px;
  margin-right: auto;
}
</style>
